import { ApplicationRef, EnvironmentInjector } from '@angular/core';
import { CompactType, DisplayGrid, GridType, GridsterConfig, GridsterItemComponent } from 'angular-gridster2';
import { WindowManager, PanelDescriptor, SerializedLayout } from './window-manager';
import { WidgetService } from '../widget.service';
import { SettingsService } from '../settings.service';

export type DockPosition = 'left' | 'right' | 'top' | 'bottom';

export interface DockedWidget {
  widgetId: string;
  position: DockPosition;
  size: number;
}

/**
 * GridsterWindowManager — wraps angular-gridster2 behind the WindowManager interface.
 *
 * This is the extraction of grid-page's gridster config, item management,
 * docking logic, and layout persistence.
 */
export class GridsterWindowManager extends WindowManager {
  private _maxRows = 1000;
  private _maxCols = 1000;
  private _nextLayerIndex = 0;

  /** The live widget array — gridster iterates this directly */
  public items: WidgetService.Widget[] = [];

  /** Gridster config — exposed for the template's [options] binding */
  public options: GridsterConfig;

  /** Docked widget state (absorbed from DockingService) */
  private _dockedWidgets: Map<DockPosition, DockedWidget[]> = new Map([
    ['left', []],
    ['right', []],
    ['top', []],
    ['bottom', []]
  ]);

  constructor(
    private _settings: SettingsService
  ) {
    super();

    this.options = {
      gridType: GridType.Fixed,
      displayGrid: DisplayGrid.None,
      compactType: CompactType.None,
      pushItems: true,
      draggable: {
        enabled: true,
        stop: this._dragStop.bind(this),
        start: this._dragStart.bind(this)
      },
      resizable: {
        enabled: true,
        stop: this._resizeStop.bind(this),
        start: this._resizeStart.bind(this)
      },
      itemResizeCallback: this._resize.bind(this),
      margin: 0,
      fixedColWidth: 20,
      fixedRowHeight: 20,
      minCols: 50,
      maxCols: this._maxCols,
      minRows: 50,
      maxRows: this._maxRows,
      maxItemCols: this._maxCols,
      minItemCols: 1,
      maxItemRows: this._maxRows,
      minItemRows: 1,
      maxItemArea: this._maxRows * this._maxCols,
      minItemArea: 1,
      defaultItemCols: 1,
      defaultItemRows: 1,
      allowMultiLayer: true,
      addEmptyRowsCount: 2
    };
  }

  // -- WindowManager interface --

  init(container: HTMLElement): void {
    // Gridster is initialized by Angular's template binding, not imperatively.
    // This is a no-op for gridster.
  }

  dispose(): void {
    this.items = [];
  }

  addPanel(descriptor: PanelDescriptor): string {
    const name = descriptor.component as WidgetService.WidgetName;
    const component = WidgetService.REGISTRY[name];
    if (!component) {
      console.warn(`[GridsterWindowManager] Unknown widget: ${name}`);
      return descriptor.id;
    }

    const id = descriptor.id;
    const size = descriptor.initialSize || {};

    const nextLayerIndex = this.items.length > 0
      ? Math.max(...this.items.map(w => (w.layerIndex ?? 0) + 1))
      : 0;

    const widget: WidgetService.Widget = {
      x: size.x ?? 1,
      y: size.y ?? 1,
      rows: size.rows ?? 35,
      cols: size.cols ?? 55,
      defaultSpace: {
        rows: size.rows ?? 35,
        cols: size.cols ?? 55
      },
      layerIndex: nextLayerIndex,
      component,
      name,
      id,
      inputs: { ...descriptor.inputs },
      outputs: { ...descriptor.outputs },
      birthday: new Date(),
      savedCoords: [0, 0, 0, 0],
      isMaximized: false
    };

    this.items.push(widget);
    return id;
  }

  removePanel(id: string): void {
    // Undock first if docked
    this._undock(id);

    const idx = this.items.findIndex(w => w.id === id);
    if (idx !== -1) {
      // setTimeout to avoid gridster-preview ghost artifacts
      setTimeout(() => {
        this.items.splice(idx, 1);
      });
    }
  }

  hasPanel(id: string): boolean {
    return this.items.some(w => w.id === id);
  }

  focusPanel(id: string): void {
    this.bringToFront(id);
  }

  getLayout(): SerializedLayout {
    return {
      type: 'gridster',
      version: 1,
      data: {
        widgets: JSON.parse(JSON.stringify(this.items)),
        docked: this._serializeDockedWidgets()
      }
    };
  }

  restoreLayout(layout: SerializedLayout): void {
    if (layout.type !== 'gridster') return;
    // Layout restoration is handled by grid-page's loadDashboard for now,
    // since it needs to call the widget-specific addFooWidget methods
    // to wire up outputs. This will be simplified later.
  }

  clear(): void {
    this.items = [];
    this._dockedWidgets.forEach((_, key) => this._dockedWidgets.set(key, []));
  }

  layout(): void {
    this.options.api?.optionsChanged();
  }

  // -- Gridster-specific public API (used by grid-page template) --

  /** Get a widget by ID from the items array */
  public getWidget(id: string): WidgetService.Widget | undefined {
    return this.items.find(w => w.id === id);
  }

  /** Update a widget's position/size after drag/resize */
  public updateWidget(id: string, widget: WidgetService.Widget): void {
    const idx = this.items.findIndex(w => w.id === id);
    if (idx !== -1) {
      this.items[idx] = widget;
    }
  }

  /** Maximize a widget to fill the viewport */
  public maximizeWidget(id: string): void {
    const found = this.items.find(w => w.id === id);
    if (!found) return;

    if (!found.isMaximized) {
      found.savedCoords = [found.x, found.y, found.cols, found.rows];
    }

    found.x = 0;
    found.y = 0;
    found.isMaximized = true;
    found.inputs.isMaximized = true;

    let { innerHeight, innerWidth } = window;
    if (this._settings.menubarPosition === 'top') {
      innerHeight -= 56;
    } else if (this._settings.menubarPosition === 'left') {
      innerWidth -= 56;
    }

    found.cols = Math.floor(innerWidth / this.options.fixedColWidth);
    found.rows = Math.floor(innerHeight / this.options.fixedRowHeight);
  }

  /** Restore a maximized widget to its saved position */
  public restoreWidget(id: string): void {
    const found = this.items.find(w => w.id === id);
    if (!found) return;

    if (found.savedCoords.reduce((sum, a) => sum + a, 0) === 0) {
      found.cols = found.defaultSpace.cols;
      found.rows = found.defaultSpace.rows;
    } else {
      found.x = found.savedCoords[0];
      found.y = found.savedCoords[1];
      found.cols = found.savedCoords[2];
      found.rows = found.savedCoords[3];
    }
    found.savedCoords = [0, 0, 0, 0];
    found.isMaximized = false;
    found.inputs.isMaximized = false;
  }

  /** Toggle maximize/restore */
  public toggleWidget(id: string): void {
    const found = this.items.find(w => w.id === id);
    if (!found) return;
    if (found.isMaximized) {
      this.restoreWidget(id);
    } else {
      this.maximizeWidget(id);
    }
  }

  /** Bring widget to front (z-index) */
  public bringToFront(id: string): void {
    const found = this.items.find(w => w.id === id);
    if (!found) return;

    this._nextLayerIndex = Math.max(this._nextLayerIndex, ...this.items.map(x => x.layerIndex ?? 0)) + 1;
    found.layerIndex = this._nextLayerIndex;
  }

  /** Get the next layer index for z-ordering */
  public get nextLayerIndex(): number {
    this._nextLayerIndex = Math.max(this._nextLayerIndex, ...this.items.map(x => x.layerIndex ?? 0)) + 1;
    return this._nextLayerIndex;
  }

  // -- Docking (absorbed from DockingService) --

  public dock(widgetId: string, position: DockPosition): void {
    this._undock(widgetId);

    const widget = this.items.find(w => w.id === widgetId);
    if (!widget) return;

    const widgets = this._dockedWidgets.get(position) || [];
    const newSize = 100 / (widgets.length + 1);
    widgets.forEach(dw => dw.size = newSize);

    if (widget.inputs) {
      widget.inputs = { ...widget.inputs, isDocked: true };
    }

    widgets.push({ widgetId, position, size: newSize });
    this._dockedWidgets.set(position, widgets);
    this._saveDockedWidgets();
  }

  public undock(widgetId: string): void {
    this._undock(widgetId);
  }

  private _undock(widgetId: string): void {
    for (const [position, widgets] of this._dockedWidgets.entries()) {
      const idx = widgets.findIndex(dw => dw.widgetId === widgetId);
      if (idx !== -1) {
        widgets.splice(idx, 1);

        const widget = this.items.find(w => w.id === widgetId);
        if (widget?.inputs) {
          widget.inputs = { ...widget.inputs, isDocked: false };
        }

        if (widgets.length > 0) {
          const newSize = 100 / widgets.length;
          widgets.forEach(dw => dw.size = newSize);
        }

        this._dockedWidgets.set(position, widgets);
        this._saveDockedWidgets();
        return;
      }
    }
  }

  public isWidgetDocked(widgetId: string): boolean {
    for (const widgets of this._dockedWidgets.values()) {
      if (widgets.some(dw => dw.widgetId === widgetId)) return true;
    }
    return false;
  }

  public getWidgetDockPosition(widgetId: string): DockPosition | null {
    for (const [position, widgets] of this._dockedWidgets.entries()) {
      if (widgets.some(dw => dw.widgetId === widgetId)) return position;
    }
    return null;
  }

  public getDockedWidgets(position: DockPosition): DockedWidget[] {
    return this._dockedWidgets.get(position) || [];
  }

  public hasDocked(position: DockPosition): boolean {
    return (this._dockedWidgets.get(position) || []).length > 0;
  }

  public getDockZoneSize(position: DockPosition): number {
    const widgets = this._dockedWidgets.get(position) || [];
    if (widgets.length === 0) return 0;
    const isHorizontal = position === 'left' || position === 'right';
    return isHorizontal
      ? Math.floor(window.innerWidth * 0.5)
      : Math.floor(window.innerHeight * 0.4);
  }

  public getDockedWidgetIndex(widgetId: string): number {
    const position = this.getWidgetDockPosition(widgetId);
    if (!position) return 0;
    return this.getDockedWidgets(position).findIndex(dw => dw.widgetId === widgetId);
  }

  public getDockedWidgetCount(position: DockPosition | null): number {
    if (!position) return 0;
    return this.getDockedWidgets(position).length;
  }

  /** Restore docked widgets from localStorage after dashboard load */
  public restoreDockedState(): void {
    const stored = localStorage.getItem('docked-widgets');
    if (!stored) return;

    try {
      const data = JSON.parse(stored) as Record<DockPosition, { widgetId: string; size: number }[]>;
      for (const position of ['left', 'right', 'top', 'bottom'] as DockPosition[]) {
        const entries = data[position] || [];
        for (const { widgetId, size } of entries) {
          const widget = this.items.find(w => w.id === widgetId);
          if (widget) {
            if (widget.inputs) {
              widget.inputs = { ...widget.inputs, isDocked: true };
            }
            const widgets = this._dockedWidgets.get(position) || [];
            widgets.push({ widgetId, position, size });
            this._dockedWidgets.set(position, widgets);
          }
        }
      }
    } catch { }
  }

  // -- Persistence --

  /** Save widget layout to localStorage */
  public saveLayout(teamName: string): void {
    localStorage.setItem(`${teamName}-widgets`, JSON.stringify(this.items));
  }

  private _saveDockedWidgets(): void {
    const data: Record<DockPosition, { widgetId: string; size: number }[]> = {
      left: [], right: [], top: [], bottom: []
    };
    this._dockedWidgets.forEach((widgets, position) => {
      data[position] = widgets.map(dw => ({ widgetId: dw.widgetId, size: dw.size }));
    });
    localStorage.setItem('docked-widgets', JSON.stringify(data));
  }

  private _serializeDockedWidgets(): Record<string, { position: DockPosition; size: number }> {
    const result: Record<string, { position: DockPosition; size: number }> = {};
    this._dockedWidgets.forEach((widgets, position) => {
      for (const dw of widgets) {
        result[dw.widgetId] = { position, size: dw.size };
      }
    });
    return result;
  }

  // -- Gridster event handlers --

  private _dragStop(item: WidgetService.Widget, itemComponent: GridsterItemComponent, event: any): void {
    this._restoreIframePointerEvents(itemComponent);
    this._panelEvent$.next({ type: 'resize', panelId: item.id, data: { item, itemComponent } });
  }

  private _dragStart(item: WidgetService.Widget, itemComponent: GridsterItemComponent, event: any): void {
    this._blockIframePointerEvents(itemComponent);
  }

  private _resizeStop(item: WidgetService.Widget, itemComponent: GridsterItemComponent, event: any): void {
    this._restoreIframePointerEvents(itemComponent);
  }

  private _resizeStart(item: WidgetService.Widget, itemComponent: GridsterItemComponent, event: any): void {
    this._blockIframePointerEvents(itemComponent);
  }

  private _resize(item: WidgetService.Widget, itemComponent: GridsterItemComponent): void {
    this._panelEvent$.next({ type: 'resize', panelId: item.id, data: { item, itemComponent } });
    const event = new Event(`resize:${item.id}`);
    window.dispatchEvent(event);
  }

  // -- iframe collision prevention (moved from grid-page) --

  private _findNestedIframe(collection: any[]): HTMLIFrameElement | undefined {
    for (let i = 0; i < collection.length; i++) {
      const element = collection[i];
      if (element.tagName === 'IFRAME') return element;
      if (element.children.length > 0) {
        const nested = this._findNestedIframe(element.children);
        if (nested) return nested;
      }
    }
    return undefined;
  }

  private _setIframePointerEvents(itemComponent: GridsterItemComponent, enabled: boolean): void {
    const children = itemComponent.el.children;
    const widget = Array.from(children).find(e => e.nodeName.endsWith('-WIDGET')) as HTMLDivElement | undefined;
    if (!widget) return;

    const iframe = this._findNestedIframe(Array.from(widget.children));
    if (!iframe) return;

    iframe.style.pointerEvents = enabled ? 'initial' : 'none';
  }

  private _blockIframePointerEvents(itemComponent: GridsterItemComponent): void {
    this._setIframePointerEvents(itemComponent, false);
  }

  private _restoreIframePointerEvents(itemComponent: GridsterItemComponent): void {
    this._setIframePointerEvents(itemComponent, true);
  }
}
